import axios from 'axios';

// Cache key is suffixed with the frontend build hash so a deploy
// implicitly invalidates the prior cache (v2 spec §11.3). Build hash
// is injected at compile time via webpack DefinePlugin in
// frontend/vue.config.js as VUE_APP_BUILD_HASH; in development with
// no hash, falls back to "dev" so the cache still works in HMR.
const BUILD_HASH = (typeof process !== 'undefined' && process.env && process.env.VUE_APP_BUILD_HASH) || 'dev';
const CACHE_KEY = `pricing_rules_v2::${BUILD_HASH}`;
// Matches the backend pricing-service cache window (60 s). Keeping the two
// windows equal bounds how far the price a user is shown can drift from the
// price the backend charges at commit time. A long client cache (it used to
// be 24 h) meant a pricing change could leave the displayed cost stale for a
// full day while the backend already charged the new amount.
const CACHE_TTL_MS = 60 * 1000;

const FALLBACK_RULES = {
  trial: {
    upload: 0,
    upload_premium: null, // Premium tier is paywalled for trial users.
    open_studio: 0,
    studio_step_image: null,
    studio_step_widget_refine: null,
    studio_branch: null,
    studio_re_anchor: null,
    export_pdf_studio: null,
  },
  paid: {
    upload: 1,
    upload_premium: 2,
    open_studio: 0,
    studio_step_image: 1,
    studio_step_widget_refine: 1,
    studio_branch: 1,
    studio_re_anchor: 1,
    export_pdf_studio: 0,
  },
};

// Pricing is keyed by tier only — credit costs do not vary by locale.
function readCache(tier) {
  try {
    const raw = window?.localStorage?.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed?.tier !== tier) return null;
    if (typeof parsed.fetchedAt !== 'number') return null;
    if (Date.now() - parsed.fetchedAt > CACHE_TTL_MS) return null;
    if (typeof parsed.rules !== 'object' || parsed.rules === null) return null;
    return parsed.rules;
  } catch (error) {
    return null;
  }
}

function writeCache(tier, rules) {
  try {
    window?.localStorage?.setItem(CACHE_KEY, JSON.stringify({
      tier,
      rules,
      fetchedAt: Date.now(),
    }));
  } catch (error) {
    // localStorage may be unavailable in private mode — swallow.
  }
}

export async function loadPricingRules(tier = 'trial', { force = false } = {}) {
  // `force` skips the (still-valid) cache — used when recovering from a
  // PRICE_CHANGED response, where the whole point is to pull the new price
  // rather than re-read the stale entry that caused the mismatch.
  if (!force) {
    const cached = readCache(tier);
    if (cached) return cached;
  }

  try {
    const response = await axios.get('/api/pricing-rules', { params: { tier } });
    const rules = response?.data?.data?.rules;
    if (rules && typeof rules === 'object') {
      writeCache(tier, rules);
      return rules;
    }
  } catch (error) {
    console.warn('Pricing rules fetch failed, using fallback:', error?.message);
  }

  return FALLBACK_RULES[tier] || FALLBACK_RULES.trial;
}

export function getFallbackRules(tier = 'trial') {
  return FALLBACK_RULES[tier] || FALLBACK_RULES.trial;
}

export function clearPricingCache() {
  try {
    window?.localStorage?.removeItem(CACHE_KEY);
  } catch (error) {
    // ignore
  }
}
